/* @jsx h */
import { defineComponent, h, ref, computed, watchEffect } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useToast } from "@app/vue/providers/Toast";
import { useAuth } from "@app/vue/providers/Auth";
import { useAcceptRequestMutation } from "./composables/useReservationRequestMutations";
import { computeTableAvailability } from "./composables/useTableAvailability";
import { listReservations } from "../../utils/trpcReservations";
import type { TableWithAvailability } from "./composables/useTableAvailability";
import { type TableSelectorTable } from "@app/vue/components/Business/TableSelector";
import { CustomerProfileBadge } from "@app/vue/components/Business/CustomerProfileBadge";
import { ExternalLink } from "@app/vue/components/ExternalLink";
import {
  ReservationRequest,
  TableStatus,
  Reservation,
  RestaurantCustomer,
  FloorPlan,
} from "@lemmonhq/types";
import { RejectReservationRequest } from "./RejectReservationRequest";
import { TableSelectionPanel } from "./TableSelectionPanel";
import { IgnoreReservationRequestModal } from "./IgnoreReservationRequestModal";
import { getBooleanSetting } from "@app/data/helpers/settings_helpers";

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h${m}` : `${h}h`;
}

// TableWithAvailability is now imported from composables/useTableAvailability

export interface AcceptReservationRequestProps {
  request: ReservationRequest;
  isOpen: boolean;
  onClose: () => void;
  onAccept?: (request: ReservationRequest) => void;
  onReject?: (request: ReservationRequest) => void;
  onIgnore?: (request: ReservationRequest) => void;
  handleActionComplete: (
    updatedRequest?: ReservationRequest,
    newReservation?: Reservation,
  ) => void;
  /** Tables floor-plan context. When set, shows pre-selected tables read-only instead of the inline search panel. */
  mapMode?: boolean;
  mapSelectedTables?: { id: number; name: string }[];
  onAssignOnMap?: () => void;
}

export const AcceptReservationRequest =
  defineComponent<AcceptReservationRequestProps>({
    name: "AcceptReservationRequest",
    props: [
      "request",
      "isOpen",
      "onClose",
      "onAccept",
      "onReject",
      "onIgnore",
      "handleActionComplete",
      "mapMode",
      "mapSelectedTables",
      "onAssignOnMap",
    ],
    setup(props) {
      const toast = useToast();
      const { user } = useAuth();
      const { t } = useLocalization();
      const trpc = useTrpc();
      const timezone = user.value?.restaurant?.timezone || "UTC";

      // State
      const loading = ref(false);
      const submitting = ref(false);
      const ignoring = ref(false);
      const allTables = ref<TableStatus[]>([]);
      const dayReservations = ref<Reservation[]>([]);
      const selectedTableIds = ref<number[]>([]);
      const durationMins = ref<number>(60);
      const error = ref("");
      const showRejectSheet = ref(false);
      const showIgnoreConfirmation = ref(false);
      const floorPlans = ref<FloorPlan[]>([]);
      const kitchenNote = ref(false);
      const guestNote = ref("");
      const showDurationToGuest = ref(true);

      // Get default time slot from restaurant settings
      const defaultTimeSlotMinutes = computed(() => {
        const timeSlotSetting = user.value?.restaurant_settings?.find(
          (s) => s.key === "reservations_time_slot_minutes",
        );
        return timeSlotSetting
          ? parseInt(timeSlotSetting.value as string, 10) || 60
          : 60;
      });

      // Restaurant-wide default for whether guests see the reservation
      // duration; the accept dialog's toggle starts from this value.
      const restaurantHidesDuration = computed(() =>
        getBooleanSetting(
          user.value?.restaurant_settings || [],
          "reservations_hide_duration_to_guests",
        ),
      );

      // Calculate tables with availability based on current duration
      const tablesWithAvailability = computed<TableWithAvailability[]>(() =>
        computeTableAvailability(allTables.value, dayReservations.value, {
          time: props.request.requested_time,
          duration_mins: durationMins.value,
        }),
      );

      const getRoom = (tableId: number) => {
        for (const fp of floorPlans.value) {
          if (fp.plan?.some((t) => t.tableId === tableId))
            return { name: fp.name, rank: fp.rank ?? 999 };
        }
        return { name: undefined, rank: 999 };
      };

      const tableSelectorData = computed<TableSelectorTable[]>(() =>
        tablesWithAvailability.value.map(
          ({ table, hasConflict, reservations }) => {
            const room = getRoom(table.id);
            return {
              id: table.id,
              name: table.table.name,
              roomName: room.name,
              roomRank: room.rank,
              availability: hasConflict ? "busy" : "free",
              reservationsCount: reservations.length,
              disabled: submitting.value,
            };
          },
        ),
      );

      const availableTablesCount = computed(
        () => tablesWithAvailability.value.filter((t) => t.isAvailable).length,
      );

      // Load initial data when modal opens
      const loadInitialData = async () => {
        if (!props.isOpen) return;

        try {
          loading.value = true;
          error.value = "";

          // Load data in parallel
          const requestedDate = new Date(props.request.requested_time);

          const promises: Promise<any>[] = [
            api.getTablesMeta(),
            listReservations(trpc, {
              date: requestedDate,
              limit: "200",
            }),
            api.getFloorPlans(),
          ];

          const results = await Promise.all(promises);

          allTables.value = results[0] || [];
          dayReservations.value =
            (results[1] as { data: Reservation[] }).data || [];
          floorPlans.value = results[2] || [];
        } catch (e) {
          console.error("Failed to load data:", e);
          error.value = t("reservations.accept.failed_load");
          allTables.value = [];
          dayReservations.value = [];
        } finally {
          loading.value = false;
        }
      };

      const acceptMutation = useAcceptRequestMutation();

      const handleAccept = async () => {
        try {
          submitting.value = true;
          const tableIds = selectedTableIds.value;

          const result = await acceptMutation.mutate({
            request_id: props.request.id,
            table_ids: tableIds.length > 0 ? tableIds : undefined,
            duration_mins: durationMins.value,
            kitchen_note: kitchenNote.value,
            message: guestNote.value || undefined,
            hide_duration: !showDurationToGuest.value,
          });

          toast.showToast(t("reservations.accept.confirmed_success"), {
            type: "success",
            autoHide: 4000,
          });

          const updatedRequest = result.request;
          const newReservation = result.reservation ?? undefined;

          // Call onAccept if provided
          if (props.onAccept) {
            props.onAccept(updatedRequest);
          }

          props.handleActionComplete(updatedRequest, newReservation);
          props.onClose();
        } catch (e) {
          console.error("Failed to accept request:", e);
          toast.showToast(
            e instanceof Error
              ? e.message
              : t("reservations.accept.confirm_failed"),
            { type: "error" },
          );
        } finally {
          submitting.value = false;
        }
      };

      const handleIgnore = async () => {
        if (ignoring.value) return;

        try {
          ignoring.value = true;
          const updatedRequest =
            await trpc.reservations.updateRequestStatus.mutate({
              request_id: props.request.id,
              status: "ignored",
            });

          toast.showToast(t("reservations.request.ignored_success"), {
            type: "success",
            autoHide: 3000,
          });

          props.onIgnore?.(updatedRequest);
          props.handleActionComplete(updatedRequest);
          showIgnoreConfirmation.value = false;
          props.onClose();
        } catch (e) {
          console.error("Failed to ignore request:", e);
          toast.showToast(t("reservations.request.ignore_failed"), {
            type: "error",
          });
        } finally {
          ignoring.value = false;
        }
      };

      // Single effect to handle modal open/close
      watchEffect(() => {
        if (props.isOpen) {
          // Reset state
          selectedTableIds.value = [];
          durationMins.value =
            props.request.duration_mins || defaultTimeSlotMinutes.value;
          error.value = "";
          showRejectSheet.value = false;
          showIgnoreConfirmation.value = false;
          kitchenNote.value = false;
          guestNote.value = "";
          // Edit requests seed from the target reservation's current
          // override (if any) so accepting doesn't silently clobber a
          // per-reservation choice with the restaurant default.
          showDurationToGuest.value =
            props.request.request_kind === "edit"
              ? !(
                  props.request.target_reservation_hide_duration ??
                  restaurantHidesDuration.value
                )
              : !restaurantHidesDuration.value;

          // Load data
          loadInitialData();
        } else {
          // Clean up when closed
          allTables.value = [];
          dayReservations.value = [];
          showIgnoreConfirmation.value = false;
        }
      });

      const isEditRequest = computed(
        () => props.request.request_kind === "edit",
      );

      return () => (
        <>
          {!showRejectSheet.value ? (
            <RightSheet
              title={
                <div class="flex items-center gap-2.5 flex-wrap">
                  <span class="text-accent font-bold text-base">
                    {new Date(props.request.requested_time).toLocaleDateString(
                      undefined,
                      {
                        weekday: "long",
                        month: "short",
                        day: "numeric",
                      },
                    )}
                  </span>
                  <span class="text-accent font-bold text-base">
                    {new Date(props.request.requested_time).toLocaleTimeString(
                      undefined,
                      {
                        hour: "numeric",
                        minute: "2-digit",
                        timeZone: timezone,
                      },
                    )}
                  </span>
                  {isEditRequest.value ? (
                    <span class="px-2.5 py-1 rounded-md text-xs font-semibold leading-5 bg-sky-500/15 text-sky-400">
                      {t("reservations.accept.change_request_badge")}
                    </span>
                  ) : (
                    <span class="px-2.5 py-1 rounded-md text-xs font-semibold leading-5 bg-error/15 text-error">
                      {t("reservations.status.pending")}
                    </span>
                  )}
                </div>
              }
              isOpen={props.isOpen}
              onClose={props.onClose}
              width="480px"
              height="calc(100dvh - 2rem)"
              mobileVariant="full-screen"
            >
              <div class="flex flex-col h-full min-h-0 overflow-hidden">
                <div class="flex-1 min-h-0 overflow-y-auto">
                  <div class="px-5 py-5 space-y-5">
                    {/* ── Line 1: Name | Guests ── */}
                    <div class="flex items-center gap-3">
                      <span class="text-accent font-semibold text-base truncate">
                        {props.request.party_name ||
                          props.request.customer_name ||
                          t("reservations.general.guest_label")}
                      </span>
                      <div class="flex items-center gap-3 flex-shrink-0">
                        <span class="flex items-center gap-1 text-sm text-base-content/70">
                          <span class="material-symbols-outlined text-base text-base-content/60">
                            group
                          </span>
                          {props.request.party_size}
                        </span>
                      </div>
                    </div>

                    {/* ── Line 2: Email (left) + Phone (right) ── */}
                    <div class="flex items-center justify-between text-xs">
                      {(() => {
                        const email =
                          props.request.party_email ||
                          props.request.customer_email;
                        return email ? (
                          <ExternalLink
                            href={`mailto:${email}`}
                            class="flex items-center gap-1.5 text-primary/80 hover:text-primary transition-colors"
                          >
                            <span class="material-symbols-outlined text-sm">
                              mail
                            </span>
                            <span class="truncate">{email}</span>
                          </ExternalLink>
                        ) : (
                          <span class="text-base-content/30">
                            {t("reservations.general.no_email")}
                          </span>
                        );
                      })()}
                      {props.request.party_phone ? (
                        <ExternalLink
                          href={`tel:${props.request.party_phone}`}
                          class="flex items-center gap-1.5 text-primary/80 hover:text-primary transition-colors"
                        >
                          <span class="material-symbols-outlined text-sm">
                            call
                          </span>
                          <span>{props.request.party_phone}</span>
                        </ExternalLink>
                      ) : (
                        <span class="flex items-center gap-1.5 text-base-content/30">
                          <span class="material-symbols-outlined text-sm">
                            call
                          </span>
                          {t("reservations.general.no_phone")}
                        </span>
                      )}
                    </div>

                    {/* ── Customer Profile ── */}
                    {props.request.customer_id && (
                      <CustomerProfileBadge
                        customerId={props.request.customer_id}
                        email={
                          props.request.customer_email ||
                          props.request.party_email
                        }
                      />
                    )}

                    {/* ── Divider ── */}
                    <div class="border-t border-base-content/8" />

                    {/* ── Change Request Context ── */}
                    {isEditRequest.value && (
                      <div class="flex items-start gap-2 px-3 py-2 rounded-md bg-sky-500/8 border border-sky-500/15">
                        <span class="material-symbols-outlined text-sm text-sky-400 mt-0.5">
                          edit_calendar
                        </span>
                        <div>
                          <p class="text-xs text-sky-300 font-medium mb-0.5">
                            {t("reservations.accept.time_change_title")}
                          </p>
                          <p class="text-xs text-base-content/60">
                            {t("reservations.accept.time_change_body")}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* ── Requested Tags ── */}
                    {(() => {
                      const requestedTags =
                        (props.request as any).requested_tags || [];
                      return requestedTags.length > 0 ? (
                        <div>
                          <h4 class="text-xs font-semibold text-base-content/45 uppercase tracking-wider mb-2">
                            {t("reservations.details.requested_experiences")}
                          </h4>
                          <div class="flex flex-wrap gap-1.5">
                            {requestedTags.map((tag: any) => (
                              <span
                                key={tag.id}
                                class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-current/15"
                                style={{
                                  backgroundColor: tag.color
                                    ? `${tag.color}20`
                                    : "oklch(var(--b3))",
                                  color: tag.color || undefined,
                                }}
                              >
                                {tag.icon && (
                                  <span class="material-symbols-outlined text-sm">
                                    {tag.icon}
                                  </span>
                                )}
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* ── Guest Notes ── */}
                    {props.request.notes && (
                      <div class="flex items-start gap-2 px-3 py-2 rounded-md bg-teal-500/8 border border-teal-500/15">
                        <span class="material-symbols-outlined text-sm text-teal-400 mt-0.5">
                          sticky_note_2
                        </span>
                        <p class="text-xs text-base-content/70 leading-relaxed">
                          {props.request.notes}
                        </p>
                      </div>
                    )}

                    {/* ── Duration Selection ── */}
                    <div>
                      <div class="flex items-center gap-2 flex-1">
                        <span class="material-symbols-outlined text-base text-base-content/55">
                          schedule
                        </span>
                        <span class="text-xs text-accent w-[46px]">
                          {t("reservations.general.duration_label")}
                        </span>
                        <button
                          type="button"
                          class="w-7 h-7 rounded-lg border border-base-content/12 flex items-center justify-center text-base-content/50 hover:text-accent hover:border-base-content/25 active:scale-95 transition-all flex-shrink-0"
                          disabled={loading.value || submitting.value}
                          onClick={() => {
                            const opts = [30, 45, 60, 90, 120, 150, 180, 240];
                            const idx = opts.indexOf(durationMins.value);
                            if (idx > 0) durationMins.value = opts[idx - 1];
                          }}
                        >
                          <span class="material-symbols-outlined text-base">
                            remove
                          </span>
                        </button>
                        <span class="text-[13px] font-semibold text-accent w-[36px] text-center flex-shrink-0 whitespace-nowrap">
                          {formatDuration(durationMins.value)}
                        </span>
                        <button
                          type="button"
                          class="w-7 h-7 rounded-lg border border-base-content/12 flex items-center justify-center text-base-content/50 hover:text-accent hover:border-base-content/25 active:scale-95 transition-all flex-shrink-0"
                          disabled={loading.value || submitting.value}
                          onClick={() => {
                            const opts = [30, 45, 60, 90, 120, 150, 180, 240];
                            const idx = opts.indexOf(durationMins.value);
                            if (idx < opts.length - 1)
                              durationMins.value = opts[idx + 1];
                          }}
                        >
                          <span class="material-symbols-outlined text-base">
                            add
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* ── Table Assignment ── */}
                    <div>
                      <TableSelectionPanel
                        tables={tableSelectorData.value}
                        selectedIds={selectedTableIds.value}
                        onChange={(ids) => {
                          selectedTableIds.value = ids;
                        }}
                        loading={loading.value}
                        error={error.value}
                        onRetry={loadInitialData}
                        availableCount={availableTablesCount.value}
                        totalCount={allTables.value.length}
                        selectionDisabled={submitting.value}
                      />
                    </div>

                    {/* ── Show Duration to Guest ── */}
                    <label class="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        class="toggle toggle-sm mt-0.5"
                        checked={showDurationToGuest.value}
                        disabled={submitting.value}
                        onChange={(e) =>
                          (showDurationToGuest.value = (
                            e.target as HTMLInputElement
                          ).checked)
                        }
                      />
                      <span>
                        <span class="text-sm text-accent block">
                          {t("reservations.accept.show_duration_toggle")}
                        </span>
                        <span class="text-xs text-base-content/50 block mt-0.5">
                          {t("reservations.accept.show_duration_hint")}
                        </span>
                      </span>
                    </label>

                    {/* ── Kitchen Note + Guest Message ── */}
                    <div class="space-y-2.5">
                      <label class="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          class="checkbox checkbox-sm mt-0.5"
                          checked={kitchenNote.value}
                          disabled={submitting.value}
                          onChange={(e) =>
                            (kitchenNote.value = (
                              e.target as HTMLInputElement
                            ).checked)
                          }
                        />
                        <span>
                          <span class="text-sm text-accent block">
                            {t("reservations.accept.kitchen_note_toggle")}
                          </span>
                          <span class="text-xs text-base-content/50 block mt-0.5">
                            {t("reservations.accept.kitchen_note_hint")}
                          </span>
                        </span>
                      </label>
                      <textarea
                        class="textarea textarea-bordered w-full bg-base-200 resize-none text-sm"
                        rows={2}
                        placeholder={t("reservations.accept.note_placeholder")}
                        value={guestNote.value}
                        onInput={(e) =>
                          (guestNote.value = (
                            e.target as HTMLTextAreaElement
                          ).value)
                        }
                        disabled={submitting.value}
                      />
                    </div>
                  </div>
                </div>

                {/* ── Footer Actions ── */}
                <div class="flex-shrink-0 px-5 pt-3.5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] border-t border-base-content/10 bg-base-100 space-y-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary w-full"
                    onClick={() => {
                      showIgnoreConfirmation.value = true;
                    }}
                    disabled={submitting.value || ignoring.value}
                  >
                    {ignoring.value ? (
                      <span class="loading loading-spinner loading-xs"></span>
                    ) : (
                      <span class="material-symbols-outlined text-sm">
                        visibility_off
                      </span>
                    )}
                    <span class="truncate">
                      {ignoring.value
                        ? t("reservations.request.ignoring")
                        : t("reservations.general.ignore")}
                    </span>
                  </button>
                  <div class="flex gap-2 w-full">
                    <button
                      type="button"
                      class="btn btn-sm btn-error flex-1 min-w-0"
                      onClick={() => {
                        showRejectSheet.value = true;
                      }}
                      disabled={submitting.value || ignoring.value}
                    >
                      <span class="material-symbols-outlined text-sm flex-shrink-0">
                        close
                      </span>
                      <span class="truncate">
                        {t("reservations.general.decline")}
                      </span>
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-success flex-1 min-w-0"
                      onClick={handleAccept}
                      disabled={submitting.value || ignoring.value}
                    >
                      {submitting.value ? (
                        <span class="loading loading-spinner loading-xs"></span>
                      ) : (
                        <span class="material-symbols-outlined text-sm flex-shrink-0">
                          check
                        </span>
                      )}
                      <span class="truncate">
                        {submitting.value
                          ? t("reservations.accept.confirming")
                          : t("reservations.general.confirm")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </RightSheet>
          ) : null}

          {showRejectSheet.value ? (
            <RejectReservationRequest
              request={props.request}
              isOpen={showRejectSheet.value}
              onClose={() => {
                showRejectSheet.value = false;
              }}
              onReject={props.onReject}
              onRejected={(updatedRequest) => {
                showRejectSheet.value = false;
                props.onClose();
                props.handleActionComplete(updatedRequest);
              }}
            />
          ) : null}

          <IgnoreReservationRequestModal
            isOpen={showIgnoreConfirmation.value}
            loading={ignoring.value}
            onClose={() => {
              if (!ignoring.value) showIgnoreConfirmation.value = false;
            }}
            onConfirm={handleIgnore}
          />
        </>
      );
    },
  });
